/* ==========================================================================
   Brand Showcase — Creative Stage Lighting
   Mothership intro, tile flight, scroll reveal, hover tilt, live filter.
   ========================================================================== */
(function () {
  'use strict';
  if (window.customElements && customElements.get('brand-showcase')) return;

  var REDUCED = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : { matches: false };
  var FINE_POINTER = window.matchMedia ? window.matchMedia('(hover: hover) and (pointer: fine)') : { matches: false };

  class BrandShowcase extends HTMLElement {
    constructor() {
      super();
      this._onKey = this._onKey.bind(this);
      this._skip = this._skip.bind(this);
    }
  }

  BrandShowcase.prototype.connectedCallback = function () {
    window.__bsReady = true;

    this.grid = this.querySelector('[data-bs-grid]');
    this.tiles = Array.prototype.slice.call(this.querySelectorAll('[data-bs-tile]'));
    this.reveals = Array.prototype.slice.call(this.querySelectorAll('[data-bs-reveal]'));
    this.splash = this.querySelector('[data-bs-splash]');
    this.heroLogo = this.querySelector('[data-bs-hero-logo]');
    this.speed = parseFloat(this.getAttribute('data-splash-speed')) || 1;
    this._pending = [];

    this._setupFilter();
    this._setupTilt();

    var designMode = !!(window.Shopify && window.Shopify.designMode);
    var playing = false;

    if (this.splash) {
      if (designMode) {
        if (this.getAttribute('data-splash-preview') === 'true' && !REDUCED.matches) {
          this.splash.hidden = false;
          document.documentElement.classList.add('bs-splash-active');
          playing = true;
        }
      } else {
        playing = !this.splash.hidden && !REDUCED.matches; // the inline script already decided
      }
    }

    if (playing) {
      window.__bsSplashHandled = true;
      this.classList.add('has-splash');
      this._playIntro();
    } else {
      if (this.splash) { this.splash.parentNode.removeChild(this.splash); this.splash = null; }
      document.documentElement.classList.remove('bs-splash-active');
      this._setupReveal(this.tiles.concat(this.reveals));
      this.classList.add('is-ready');
      this._countUp(0);
    }
  };

  BrandShowcase.prototype.disconnectedCallback = function () {
    document.removeEventListener('keydown', this._onKey);
    if (this.io) { this.io.disconnect(); this.io = null; }
    clearTimeout(this._t1); clearTimeout(this._t2); clearTimeout(this._t3);
    document.documentElement.classList.remove('bs-splash-active');
  };

  /* ---------------------------------------------------------------------- */
  /* Intro                                                                  */
  /* ---------------------------------------------------------------------- */
  BrandShowcase.prototype._playIntro = function () {
    var self = this;
    var splash = this.splash;
    var skipBtn = splash.querySelector('[data-bs-splash-skip]');
    var s = this.speed;

    if (skipBtn) skipBtn.addEventListener('click', this._skip);
    splash.addEventListener('click', this._skip);
    document.addEventListener('keydown', this._onKey);

    this._t1 = setTimeout(function () { splash.classList.add('is-charged'); }, 950 * s);
    this._t2 = setTimeout(function () { self._deploy(false); }, 1650 * s);
  };

  BrandShowcase.prototype._onKey = function (e) {
    if (e.key === 'Escape' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      this._skip();
    }
  };

  BrandShowcase.prototype._skip = function () {
    if (this._finished) return;
    if (this._deployed) { this._finish(true); return; }
    clearTimeout(this._t1); clearTimeout(this._t2);
    this._deploy(true);
  };

  BrandShowcase.prototype._deploy = function (fast) {
    if (this._deployed) return;
    this._deployed = true;

    var self = this;
    var splash = this.splash;
    var grid = this.grid;
    var logo = splash ? splash.querySelector('[data-bs-splash-logo]') : null;
    var vw = window.innerWidth, vh = window.innerHeight;
    var cx = vw / 2, cy = vh / 2;
    var stagger = fast ? 10 : Math.round(32 * this.speed);
    var flight = fast ? 500 : Math.round(1150 * this.speed);

    /* 1. From-state: in-view tiles start at the mothership; the rest reveal on scroll later */
    var flyers = [];
    var pending = [];
    this.tiles.forEach(function (t, i) {
      var r = t.getBoundingClientRect();
      if (r.bottom < -40 || r.top > vh + 40) { pending.push(t); return; }
      var dx = cx - (r.left + r.width / 2);
      var dy = cy - (r.top + r.height / 2);
      var spin = (i % 2 ? 1 : -1) * (6 + (i % 5) * 2);
      t.style.transition = 'none';
      t.style.transform = 'translate3d(' + dx.toFixed(1) + 'px,' + dy.toFixed(1) + 'px,0) scale(0.1) rotate(' + spin + 'deg)';
      t.style.opacity = '0';
      flyers.push(t);
    });
    this.reveals.forEach(function (el) { pending.push(el); });
    pending.forEach(function (el) { el.classList.add('is-pending'); });
    this._pending = pending;

    /* 2. Where the logo lands (FLIP onto the hero logo) */
    var target = null;
    if (logo && this.heroLogo) {
      var a = logo.getBoundingClientRect();
      var b = this.heroLogo.getBoundingClientRect();
      if (a.width > 0 && b.width > 0 && b.top < vh && b.bottom > 0) {
        target = {
          dx: (b.left + b.width / 2) - (a.left + a.width / 2),
          dy: (b.top + b.height / 2) - (a.top + a.height / 2),
          scale: b.width / a.width
        };
      }
    }

    /* 3. Release the page: CSS targets become "visible", hero entrance starts slightly later */
    this.style.setProperty('--hero-delay', Math.round((fast ? 150 : 420) * this.speed) + 'ms');
    document.documentElement.classList.remove('bs-splash-active');
    this.classList.add('is-ready');
    if (splash) splash.classList.add('is-deploying');

    /* commit the from-state before switching to the end-state */
    void (grid ? grid.offsetWidth : document.body.offsetWidth);

    /* 4. Launch */
    if (grid) grid.classList.add('is-deploying');
    flyers.forEach(function (t, i) {
      t.style.transition = '';
      t.style.transitionDelay = Math.round(i * stagger) + 'ms';
      if (fast) t.style.transitionDuration = flight + 'ms, 350ms, 400ms';
      t.style.transform = '';
      t.style.opacity = '';
    });

    /* 5. Logo morph */
    if (logo) {
      logo.classList.add('is-morphing');
      void logo.offsetWidth;
      if (target) {
        logo.style.transform =
          'translate(calc(-50% + ' + target.dx.toFixed(1) + 'px), calc(-50% + ' + target.dy.toFixed(1) + 'px)) scale(' + target.scale.toFixed(4) + ')';
      } else {
        logo.classList.add('is-gone');
      }
    }

    this._countUp(Math.round((fast ? 150 : 500) * this.speed));

    var total = flight + flyers.length * stagger + 120;
    this._t3 = setTimeout(function () { self._finish(false); }, total);
  };

  BrandShowcase.prototype._finish = function (cut) {
    if (this._finished) return;
    this._finished = true;
    var self = this;
    clearTimeout(this._t3);
    document.removeEventListener('keydown', this._onKey);

    if (cut && this.grid) this.grid.classList.remove('is-deploying');
    this.tiles.forEach(function (t) {
      t.style.transition = '';
      t.style.transitionDelay = '';
      t.style.transitionDuration = '';
      t.style.transform = '';
      t.style.opacity = '';
    });
    if (this.heroLogo) this.heroLogo.classList.add('is-landed');

    var splash = this.splash;
    if (splash) {
      var logo = splash.querySelector('[data-bs-splash-logo]');
      if (logo) logo.classList.add('is-gone');
      setTimeout(function () {
        if (splash.parentNode) splash.parentNode.removeChild(splash);
        if (self.splash === splash) self.splash = null;
      }, cut ? 0 : 520);
    }
    if (!cut && this.grid) {
      setTimeout(function () { if (self.grid) self.grid.classList.remove('is-deploying'); }, 60);
    }
    this._setupReveal(this._pending);
    this._pending = [];
  };

  /* ---------------------------------------------------------------------- */
  /* Scroll reveal                                                          */
  /* ---------------------------------------------------------------------- */
  BrandShowcase.prototype._setupReveal = function (elements) {
    var self = this;
    var els = (elements || []).filter(Boolean);
    if (!els.length) return;
    if (!('IntersectionObserver' in window) || REDUCED.matches) {
      els.forEach(function (el) { el.classList.remove('is-pending'); });
      return;
    }
    els.forEach(function (el) { el.classList.add('is-pending'); });

    var batch = [];
    var raf = 0;
    if (!this.io) {
      this.io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          self.io.unobserve(entry.target);
          batch.push(entry.target);
        });
        if (batch.length && !raf) {
          raf = requestAnimationFrame(function () {
            batch.sort(function (a, b) {
              var ra = a.getBoundingClientRect(), rb = b.getBoundingClientRect();
              return (ra.top - rb.top) || (ra.left - rb.left);
            });
            batch.forEach(function (el, i) {
              el.style.setProperty('--rd', (Math.min(i, 14) * 45) + 'ms');
              el.classList.remove('is-pending');
              el.classList.add('is-revealing');
              el.addEventListener('animationend', function () {
                el.classList.remove('is-revealing');
                el.style.removeProperty('--rd');
              }, { once: true });
            });
            batch = [];
            raf = 0;
          });
        }
      }, { rootMargin: '4000px 0px -6% 0px', threshold: 0.02 }); /* big top margin: anything scrolled past is revealed too */
    }
    els.forEach(function (el) { self.io.observe(el); });
  };

  /* ---------------------------------------------------------------------- */
  /* Counters                                                               */
  /* ---------------------------------------------------------------------- */
  BrandShowcase.prototype._countUp = function (delay) {
    var els = this.querySelectorAll('[data-bs-count]');
    if (!els.length) return;
    var lang = document.documentElement.lang || 'en';
    var fmt;
    try { fmt = new Intl.NumberFormat(lang); } catch (e) { fmt = { format: function (n) { return String(n); } }; }
    Array.prototype.forEach.call(els, function (el) {
      var target = parseInt(el.getAttribute('data-bs-count'), 10) || 0;
      if (REDUCED.matches) { el.textContent = fmt.format(target); return; }
      el.textContent = '0';
      setTimeout(function () {
        var dur = 1500;
        var start = null;
        var tick = function (now) {
          if (start === null) start = now;
          var p = Math.min(1, (now - start) / dur);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = fmt.format(Math.round(target * eased));
          if (p < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }, (delay || 0) + 350);
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Hover tilt + cursor spotlight                                          */
  /* ---------------------------------------------------------------------- */
  BrandShowcase.prototype._setupTilt = function () {
    if (!this.grid || this.getAttribute('data-tilt') !== 'true') return;
    if (!FINE_POINTER.matches || REDUCED.matches) return;

    this.grid.addEventListener('pointermove', function (e) {
      var tile = e.target.closest ? e.target.closest('[data-bs-tile]') : null;
      if (!tile) return;
      var r = tile.getBoundingClientRect();
      if (!r.width || !r.height) return;
      var px = (e.clientX - r.left) / r.width;
      var py = (e.clientY - r.top) / r.height;
      tile.style.setProperty('--mx', (px * 100).toFixed(1) + '%');
      tile.style.setProperty('--my', (py * 100).toFixed(1) + '%');
      tile.style.setProperty('--ry', ((px - 0.5) * 9).toFixed(2) + 'deg');
      tile.style.setProperty('--rx', ((0.5 - py) * 9).toFixed(2) + 'deg');
    });

    this.tiles.forEach(function (tile) {
      tile.addEventListener('pointerleave', function () {
        tile.style.removeProperty('--rx');
        tile.style.removeProperty('--ry');
      });
    });
  };

  /* ---------------------------------------------------------------------- */
  /* Live filter                                                            */
  /* ---------------------------------------------------------------------- */
  BrandShowcase.prototype._setupFilter = function () {
    var input = this.querySelector('[data-bs-filter]');
    if (!input || !this.grid) return;
    var tiles = this.tiles;
    var clear = this.querySelector('[data-bs-filter-clear]');
    var status = this.querySelector('[data-bs-status]');
    var empty = this.querySelector('[data-bs-empty]');
    var total = tiles.length;

    var norm = function (str) {
      str = String(str || '');
      try { str = str.normalize('NFD').replace(/[̀-ͯ]/g, ''); } catch (e) {}
      return str.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
    };
    var index = tiles.map(function (t) {
      return norm(t.getAttribute('data-search') || t.getAttribute('data-title') || t.textContent);
    });

    var apply = function () {
      var q = norm(input.value);
      var shown = 0;
      tiles.forEach(function (t, i) {
        var hit = !q || index[i].indexOf(q) !== -1;
        t.hidden = !hit;
        if (hit) shown++;
      });
      if (clear) clear.hidden = !q;
      if (empty) empty.hidden = shown > 0;
      if (status) {
        status.textContent = q
          ? shown + ' of ' + total + (total === 1 ? ' brand' : ' brands')
          : total + (total === 1 ? ' brand' : ' brands');
      }
    };

    input.addEventListener('input', apply);
    input.addEventListener('search', apply);
    input.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { input.value = ''; apply(); }
    });
    if (clear) {
      clear.addEventListener('click', function () { input.value = ''; apply(); input.focus(); });
    }
    apply();
  };

  if (window.customElements) customElements.define('brand-showcase', BrandShowcase);
})();
